import React from 'react';
import type { MappingLevel } from '../../types/mappingDrillDown';
import { useMappingStackBreadcrumb, useMappingStackStore } from '../../store/mappingStackStore';
import { useSearchTabsStore } from '../../store/searchTabsStore';
import { clearMappingParamFromLocation } from '../../utils/mappingUrlState';
import { isEvidenceOnlyMapping } from '../../config/mappingRules';

interface MappingBreadcrumbProps {
  className?: string;
  separator?: React.ReactNode;
}

function handleBreadcrumbNavigate(clearFromLevel: (index: number) => void, levelIndex: number) {
  clearFromLevel(levelIndex + 1);
  const { stack, currentIndex } = useMappingStackStore.getState();
  const atRoot = stack.length <= 1 || currentIndex <= 0;
  if (!atRoot) return;

  clearMappingParamFromLocation();
  useSearchTabsStore.getState().flushActiveSearch(window.location.search);
}

export function MappingBreadcrumb({ className = '', separator = ' / ' }: MappingBreadcrumbProps) {
  const { stack, currentIndex, clearFromLevel } = useMappingStackBreadcrumb();

  if (stack.length === 0) {
    return null;
  }

  const items: React.ReactNode[] = [];
  stack.forEach((level: MappingLevel, index: number) => {
    const itemKey = level.id ? `${level.id}-${index}` : `level-${index}`;
    if (index > 0) {
      items.push(
        <span key={`sep-${itemKey}`} className="mx-1 text-gray-400" aria-hidden>
          {separator}
        </span>,
      );
    }
    const isCurrent = index === currentIndex;
    const mappingSuffix = (() => {
      if (!level.mappingContent) return 'results';
      return isEvidenceOnlyMapping(level.mappingContent.name) ? 'evidences' : 'results';
    })();
    const displayLabel =
      level.labelNode != null ? (
        <>
          {level.labelNode} {mappingSuffix}
        </>
      ) : level.mappingContent ? (
        `${level.label} ${mappingSuffix}`
      ) : (
        level.label
      );
    const ariaLabel = typeof level.label === 'string' ? level.label : mappingSuffix;
    if (isCurrent) {
      items.push(
        <span key={itemKey} className="font-medium text-gray-900" aria-current="location">
          {displayLabel}
        </span>,
      );
    } else {
      items.push(
        <button
          key={itemKey}
          type="button"
          onClick={() => handleBreadcrumbNavigate(clearFromLevel, index)}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
          aria-label={`Back to ${ariaLabel}`}
        >
          {displayLabel}
        </button>,
      );
    }
  });

  return (
    <nav aria-label="Mapping navigation" className={`flex flex-wrap items-center text-sm ml-1 ${className}`}>
      {items}
    </nav>
  );
}

export default MappingBreadcrumb;
